import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAction, useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch (_error) {
    return String(value || "");
  }
}

export default function AdminEmsRunEvaluations() {
  const user = useUser();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [filters, setFilters] = useState({
    sourceType: "",
    sourceSystem: "",
    status: "",
  });
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);
  const [structuredJson, setStructuredJson] = useState("");
  const [structuredSourceSystem, setStructuredSourceSystem] = useState("zoll-emscharts");
  const [artifactSourceSystem, setArtifactSourceSystem] = useState("pdf-generic");
  const [uploadedArtifact, setUploadedArtifact] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);

  const [{ data: listData, fetching: loadingList, error: listError }, listEvaluations] = useGlobalAction(api.listEmsRunEvaluations);
  const [{ data: detailData, fetching: loadingDetail, error: detailError }, getEvaluation] = useGlobalAction(api.getEmsRunEvaluation);
  const [{ fetching: evaluatingStructured }, evaluateStructured] = useGlobalAction(api.evaluateEmsRunDocumentation);
  const [{ fetching: evaluatingArtifact }, evaluateArtifact] = useGlobalAction(api.evaluateEmsRunArtifact);
  const [{ fetching: creatingUserFile }, createUserFile] = useAction(api.userFile.create);
  const [{ }, getCurrentUserAccessProfile] = useGlobalAction(api.getCurrentUserAccessProfile);

  useEffect(() => {
    listEvaluations(filters);
  }, [filters.sourceType, filters.sourceSystem, filters.status]);

  useEffect(() => {
    if (selectedEvaluationId) {
      getEvaluation({ evaluationId: selectedEvaluationId });
    }
  }, [selectedEvaluationId]);

  useEffect(() => {
    (async () => {
      try {
        const result = await getCurrentUserAccessProfile({});
        setCurrentUserProfile(result?.user ? result : result?.profile || null);
      } catch (_error) {
        setCurrentUserProfile(null);
      }
    })();
  }, []);

  const evaluations = listData?.evaluations || [];
  const selectedEvaluation = detailData?.evaluation || null;

  const stats = useMemo(() => {
    const total = evaluations.length;
    const completed = evaluations.filter((entry) => entry.status === "completed").length;
    const artifact = evaluations.filter((entry) => entry.sourceType === "artifact").length;
    const structured = evaluations.filter((entry) => entry.sourceType === "structured").length;
    return { total, completed, artifact, structured };
  }, [evaluations]);

  const handleStructuredSubmit = async () => {
    try {
      const payload = JSON.parse(structuredJson);
      const result = await evaluateStructured({
        payload,
        sourceSystem: structuredSourceSystem,
        sourceType: "structured",
        persist: true,
      });
      toast({
        title: "Structured evaluation completed",
        description: `Saved evaluation ${result.evaluationId}`,
        status: "success",
        duration: 3000,
      });
      setStructuredJson("");
      await listEvaluations(filters);
      if (result?.evaluationId) setSelectedEvaluationId(result.evaluationId);
    } catch (error) {
      toast({
        title: "Structured evaluation failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleArtifactUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const ownerId = currentUserProfile?.user?.id || currentUserProfile?.id || user?.id;
      const institution = currentUserProfile?.user?.institution || currentUserProfile?.institution || user?.institution;
      if (!ownerId || !institution) {
        throw new Error("Could not resolve current user for artifact upload");
      }

      const timestamp = Date.now();
      const created = await createUserFile({
        file: { copyFile: file },
        originalFilename: file.name,
        title: file.name,
        mimeType: file.type || "application/pdf",
        fileSizeBytes: file.size,
        category: "clinical_record",
        uploadDate: new Date().toISOString(),
        status: "active",
        isPublic: false,
        institution,
        owner: { _link: ownerId },
        studentId: ownerId,
        submissionSource: "portal",
        fileIdentifier: `ems-run-artifact-${timestamp}-${file.name}`,
        clinicalFileId: `ems-run-artifact-${timestamp}`,
      });

      setUploadedArtifact(created);
      toast({
        title: "Artifact uploaded",
        description: file.name,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Artifact upload failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleArtifactEvaluate = async () => {
    if (!uploadedArtifact?.id) return;
    try {
      const result = await evaluateArtifact({
        userFileId: uploadedArtifact.id,
        sourceSystem: artifactSourceSystem,
        useClinicalGroundTruth: true,
      });
      toast({
        title: "Artifact evaluation completed",
        description: `Saved evaluation ${result.evaluationId}`,
        status: "success",
        duration: 3000,
      });
      await listEvaluations(filters);
      if (result?.evaluationId) setSelectedEvaluationId(result.evaluationId);
    } catch (error) {
      toast({
        title: "Artifact evaluation failed",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box p={6}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Heading size="lg">EMS Run Evaluations</Heading>
          <Text color="gray.400" mt={2}>
            Parallel EMS QA/QI review for structured ePCR payloads and uploaded chart artifacts.
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <Stat><StatLabel>Total</StatLabel><StatNumber>{stats.total}</StatNumber></Stat>
          <Stat><StatLabel>Completed</StatLabel><StatNumber>{stats.completed}</StatNumber></Stat>
          <Stat><StatLabel>Structured</StatLabel><StatNumber>{stats.structured}</StatNumber></Stat>
          <Stat><StatLabel>Artifacts</StatLabel><StatNumber>{stats.artifact}</StatNumber></Stat>
        </SimpleGrid>

        <Grid templateColumns={{ base: "1fr", xl: "1.1fr 1fr" }} gap={6}>
          <GridItem>
            <VStack align="stretch" spacing={6}>
              <Card>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="md">Structured JSON</Heading>
                    <FormControl>
                      <FormLabel>Source system</FormLabel>
                      <Select value={structuredSourceSystem} onChange={(e) => setStructuredSourceSystem(e.target.value)}>
                        <option value="zoll-emscharts">Zoll emsCharts</option>
                        <option value="generic-nemsis">Generic NEMSIS</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Payload JSON</FormLabel>
                      <Textarea
                        minH="260px"
                        fontFamily="mono"
                        value={structuredJson}
                        onChange={(e) => setStructuredJson(e.target.value)}
                        placeholder='Paste structured ePCR payload JSON here'
                      />
                    </FormControl>
                    <Button
                      colorScheme="blue"
                      onClick={handleStructuredSubmit}
                      isLoading={evaluatingStructured}
                    >
                      Evaluate Structured Payload
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="md">Artifact PDF / Export</Heading>
                    <FormControl>
                      <FormLabel>Artifact profile</FormLabel>
                      <Select value={artifactSourceSystem} onChange={(e) => setArtifactSourceSystem(e.target.value)}>
                        <option value="pdf-generic">Generic PDF / export</option>
                        <option value="zoll-blackout-pdf">Zoll blackout PDF</option>
                      </Select>
                    </FormControl>
                    <Input ref={fileInputRef} type="file" accept=".pdf,.txt,.json" onChange={handleArtifactUpload} />
                    {uploadedArtifact && (
                      <Alert status="success">
                        <AlertIcon />
                        Uploaded {uploadedArtifact.originalFilename || uploadedArtifact.title || uploadedArtifact.id}
                      </Alert>
                    )}
                    <Button
                      colorScheme="teal"
                      onClick={handleArtifactEvaluate}
                      isDisabled={!uploadedArtifact?.id}
                      isLoading={creatingUserFile || evaluatingArtifact}
                    >
                      Evaluate Artifact
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Heading size="md">Recent Evaluations</Heading>
                    <HStack>
                      <Select value={filters.sourceType} onChange={(e) => setFilters((prev) => ({ ...prev, sourceType: e.target.value }))}>
                        <option value="">All source types</option>
                        <option value="structured">Structured</option>
                        <option value="artifact">Artifact</option>
                      </Select>
                      <Select value={filters.sourceSystem} onChange={(e) => setFilters((prev) => ({ ...prev, sourceSystem: e.target.value }))}>
                        <option value="">All systems</option>
                        <option value="zoll-emscharts">Zoll emsCharts</option>
                        <option value="generic-nemsis">Generic NEMSIS</option>
                        <option value="pdf-generic">Generic PDF</option>
                        <option value="zoll-blackout-pdf">Zoll blackout PDF</option>
                      </Select>
                      <Select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
                        <option value="">All statuses</option>
                        <option value="completed">Completed</option>
                        <option value="failed">Failed</option>
                      </Select>
                    </HStack>

                    {loadingList ? <Spinner /> : listError ? (
                      <Alert status="error"><AlertIcon />{listError.message}</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="sm">
                          <Thead>
                            <Tr>
                              <Th>Title</Th>
                              <Th>Source</Th>
                              <Th>Score</Th>
                              <Th>Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {evaluations.map((entry) => (
                              <Tr key={entry.id} onClick={() => setSelectedEvaluationId(entry.id)} cursor="pointer" bg={selectedEvaluationId === entry.id ? "whiteAlpha.200" : "transparent"}>
                                <Td>
                                  <Text fontWeight="medium">{entry.title}</Text>
                                  <Text fontSize="xs" color="gray.400">{entry.sourceReference || entry.originalFile?.originalFilename || ""}</Text>
                                </Td>
                                <Td>
                                  <Badge mr={2}>{entry.sourceType}</Badge>
                                  <Text fontSize="xs">{entry.sourceSystem}</Text>
                                </Td>
                                <Td>{entry.scores?.overall ?? "N/A"}</Td>
                                <Td><Badge colorScheme={entry.status === "completed" ? "green" : entry.status === "failed" ? "red" : "yellow"}>{entry.status}</Badge></Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>

          <GridItem>
            <Card h="100%">
              <CardBody>
                {loadingDetail ? (
                  <Spinner />
                ) : detailError ? (
                  <Alert status="error"><AlertIcon />{detailError.message}</Alert>
                ) : !selectedEvaluation ? (
                  <Text color="gray.400">Select an evaluation to inspect the normalized encounter and findings.</Text>
                ) : (
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Heading size="md">{selectedEvaluation.title}</Heading>
                      <HStack mt={2}>
                        <Badge>{selectedEvaluation.sourceType}</Badge>
                        <Badge>{selectedEvaluation.sourceSystem}</Badge>
                        <Badge colorScheme="green">Overall {selectedEvaluation.scores?.overall ?? "N/A"}</Badge>
                      </HStack>
                    </Box>

                    <SimpleGrid columns={2} spacing={4}>
                      <Stat><StatLabel>Clinical</StatLabel><StatNumber>{selectedEvaluation.scores?.clinicalCare ?? "N/A"}</StatNumber></Stat>
                      <Stat><StatLabel>Documentation</StatLabel><StatNumber>{selectedEvaluation.scores?.documentationQuality ?? "N/A"}</StatNumber></Stat>
                      <Stat><StatLabel>Billing</StatLabel><StatNumber>{selectedEvaluation.scores?.billingSupport ?? "N/A"}</StatNumber></Stat>
                      <Stat><StatLabel>CMS</StatLabel><StatNumber>{selectedEvaluation.scores?.cmsMedicalNecessity ?? "N/A"}</StatNumber></Stat>
                    </SimpleGrid>

                    <Divider />
                    <Box>
                      <Heading size="sm" mb={2}>Findings Summary</Heading>
                      <Text whiteSpace="pre-wrap">{selectedEvaluation.findingsSummary?.markdown || "No summary available."}</Text>
                    </Box>

                    <Box>
                      <Heading size="sm" mb={2}>Follow-up Questions</Heading>
                      <VStack align="stretch" spacing={2}>
                        {(selectedEvaluation.evaluationResult?.followUpQuestions || []).map((question, index) => (
                          <Text key={index}>- {question}</Text>
                        ))}
                      </VStack>
                    </Box>

                    <Box>
                      <Heading size="sm" mb={2}>Review Lenses</Heading>
                      <VStack align="stretch" spacing={3}>
                        {Object.entries(selectedEvaluation.evaluationResult?.reviewLenses || {}).map(([key, lens]) => (
                          <Card key={key} variant="outline">
                            <CardBody>
                              <VStack align="stretch" spacing={2}>
                                <Heading size="xs">{lens.title || key}</Heading>
                                <Text fontSize="sm" color="gray.400">{lens.perspective}</Text>
                                <Text fontSize="sm">{lens.summary}</Text>
                              </VStack>
                            </CardBody>
                          </Card>
                        ))}
                      </VStack>
                    </Box>

                    <Box>
                      <Heading size="sm" mb={2}>Scope</Heading>
                      <Code whiteSpace="pre-wrap" display="block" p={3}>
                        {prettyJson(selectedEvaluation.scopeSummary)}
                      </Code>
                    </Box>

                    <Box>
                      <Heading size="sm" mb={2}>Canonical Encounter</Heading>
                      <Code whiteSpace="pre-wrap" display="block" p={3} maxH="280px" overflowY="auto">
                        {prettyJson(selectedEvaluation.canonicalEncounter)}
                      </Code>
                    </Box>

                    <Box>
                      <Heading size="sm" mb={2}>Evaluation Result</Heading>
                      <Code whiteSpace="pre-wrap" display="block" p={3} maxH="280px" overflowY="auto">
                        {prettyJson(selectedEvaluation.evaluationResult)}
                      </Code>
                    </Box>
                  </VStack>
                )}
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      </VStack>
    </Box>
  );
}
